import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Blocks, ChevronDown, ChevronRight, MoreVertical, Plus, Settings as SettingsIcon, Archive, LogOut } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui';
import { ConfirmModal } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { useMeSpaces } from '@/hooks/useMeSpaces';
import { useUpdateSpace } from '@/hooks/useSpaceManagement';
import { useRemoveMember } from '@/hooks/useMembers';
import { useAuth } from '@/store/auth-context';
import { useSidebarCollapse } from '@/components/layout/sidebar-collapse-context';
import { routes, switchSpace } from '@/lib/routes';
import { rememberPath } from '@/lib/last-path';
import { meetsRole } from '@/lib/space-roles';
import { cn } from '@/lib/cn';
import { CreateSpaceModal } from '@/pages/spaces/CreateSpaceModal';
import type { SpaceSummary } from '@/types/spaces';

interface SpaceSwitcherProps {
  /** Currently active space id, parsed from the URL by ConsoleLayout. */
  currentSpaceId: string | undefined;
  /** Current pathname — used to compute the destination when switching. */
  currentPath: string;
  /** Sidebar collapsed state. When true, the switcher renders icon-only and
   *  clicking it expands the sidebar (mirrors the `Chat` nav item). */
  collapsed?: boolean;
}

type RowAction =
  | { kind: 'archive'; space: SpaceSummary }
  | { kind: 'leave'; space: SpaceSummary };

/**
 * Console-side space picker. Renders an inline list of spaces (similar to
 * Chat in the operator nav) so the active space and switch targets are
 * always visible without an extra click.
 *
 * In the collapsed sidebar state the switcher is just a building icon; click
 * it to expand the sidebar and reveal the list.
 */
export function SpaceSwitcher({
  currentSpaceId,
  currentPath,
  collapsed = false,
}: SpaceSwitcherProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { success, error: showError } = useToast();
  const { setCollapsed } = useSidebarCollapse();

  const [createOpen, setCreateOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<RowAction | null>(null);
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading } = useMeSpaces();
  const spaces = useMemo(() => data?.items ?? [], [data?.items]);
  const current = spaces.find((s) => s.spaceId === currentSpaceId);

  // Sort: personal first, then by name. Archived spaces are filtered out
  // server-side (useMeSpaces no longer requests them).
  const sortedSpaces = useMemo(() => {
    return [...spaces].sort((a, b) => {
      const persA = a.kind === 'personal' ? 0 : 1;
      const persB = b.kind === 'personal' ? 0 : 1;
      if (persA !== persB) return persA - persB;
      return a.name.localeCompare(b.name);
    });
  }, [spaces]);

  const updateSpace = useUpdateSpace(pendingAction?.space.spaceId);
  const removeMember = useRemoveMember(pendingAction?.space.spaceId ?? '');

  const handleSelect = (spaceId: string) => {
    // Clicking the already-active space jumps to its details page rather
    // than no-op'ing — gives the active row a useful click target.
    if (spaceId === currentSpaceId) {
      navigate(routes.console.spaceManage(spaceId));
      return;
    }
    const target = switchSpace(currentPath, spaceId);
    rememberPath(`console:${spaceId}`, target);
    navigate(target);
  };

  const confirmAction = async () => {
    if (!pendingAction) return;
    const { kind, space } = pendingAction;
    try {
      if (kind === 'archive') {
        await updateSpace.mutateAsync({ archived: true });
        success(`Archived ${space.name}`);
        if (space.spaceId === currentSpaceId) navigate('/c', { replace: true });
      } else if (kind === 'leave') {
        if (!user?.userId) throw new Error('Not signed in');
        await removeMember.mutateAsync(user.userId);
        success(`Left ${space.name}`);
        if (space.spaceId === currentSpaceId) navigate('/c', { replace: true });
      }
      setPendingAction(null);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Action failed');
    }
  };

  if (collapsed) {
    return (
      <div className="px-2">
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => setCollapsed(false)}
              aria-label={current ? `Space: ${current.name}` : 'Select space'}
              className={cn(
                'group flex w-full items-center justify-center gap-3 rounded-md px-2 py-2 text-sm font-medium transition-colors',
                'text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-ring',
              )}
            >
              <Blocks className="h-5 w-5 shrink-0" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">{current?.name ?? 'Select space'}</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  const actionPending = updateSpace.isPending || removeMember.isPending;

  // Collapsed header shows the active space name; expanded shows "Spaces".
  const headerLabel = expanded ? 'Spaces' : (current?.name ?? 'Spaces');

  return (
    <div className="space-y-1 px-2">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse spaces' : 'Expand spaces'}
          className={cn(
            'group flex flex-1 items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
            'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
            'focus-ring',
          )}
        >
          <Blocks className="h-5 w-5 shrink-0" />
          <span className="flex-1 truncate text-left">{headerLabel}</span>
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground/70" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/70" />
          )}
        </button>
        {expanded && (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            aria-label="New space"
            title="New space"
            className="focus-ring flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <Plus className="h-4 w-4" />
          </button>
        )}
      </div>

      {!expanded ? null : isLoading && spaces.length === 0 ? (
        <div className="px-3 py-1.5 text-xs text-muted-foreground/60">Loading…</div>
      ) : spaces.length === 0 ? (
        <div className="px-3 py-1.5 text-xs text-muted-foreground/60">No spaces</div>
      ) : (
        sortedSpaces.map((s) => {
          const active = s.spaceId === currentSpaceId;
          const isPersonal = s.kind === 'personal';
          const isOwner = s.role === 'owner';
          const isAdminPlus = meetsRole(s.role, 'admin');
          // Personal Space gets no kebab — nothing in it is actionable here.
          const showKebab = !isPersonal;

          return (
            <div
              key={s.spaceId}
              className="group/row flex items-center gap-1 rounded-md"
            >
              <button
                type="button"
                onClick={() => handleSelect(s.spaceId)}
                className={cn(
                  'flex flex-1 items-center gap-2 truncate rounded-md px-3 py-1.5 text-left text-xs transition-colors',
                  active
                    ? 'text-primary'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )}
                aria-current={active ? 'true' : undefined}
              >
                <span className="flex-1 truncate">{s.name}</span>
              </button>

              {showKebab && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        'shrink-0 rounded-md p-1 text-muted-foreground/60 transition-opacity',
                        'opacity-0 focus:opacity-100 focus-visible:opacity-100 group-hover/row:opacity-100',
                        'hover:bg-accent hover:text-foreground',
                        active && 'opacity-100',
                      )}
                      aria-label={`Actions for ${s.name}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[180px]">
                    {isAdminPlus && (
                      <DropdownMenuItem
                        onSelect={() => navigate(routes.console.spaceManage(s.spaceId))}
                      >
                        <SettingsIcon className="mr-2 h-3.5 w-3.5" />
                        Manage
                      </DropdownMenuItem>
                    )}
                    {isOwner && (
                      <DropdownMenuItem
                        onSelect={() => setPendingAction({ kind: 'archive', space: s })}
                        className="text-red-600 focus:text-red-700"
                      >
                        <Archive className="mr-2 h-3.5 w-3.5" />
                        Archive space
                      </DropdownMenuItem>
                    )}
                    {!isOwner && (
                      <DropdownMenuItem
                        onSelect={() => setPendingAction({ kind: 'leave', space: s })}
                        className="text-red-600 focus:text-red-700"
                      >
                        <LogOut className="mr-2 h-3.5 w-3.5" />
                        Leave space
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          );
        })
      )}

      <CreateSpaceModal isOpen={createOpen} onClose={() => setCreateOpen(false)} />

      <ConfirmModal
        isOpen={!!pendingAction}
        onClose={() => !actionPending && setPendingAction(null)}
        onConfirm={confirmAction}
        title={
          pendingAction?.kind === 'archive'
            ? `Archive ${pendingAction.space.name}?`
            : pendingAction?.kind === 'leave'
              ? `Leave ${pendingAction?.space.name}?`
              : ''
        }
        message={
          pendingAction?.kind === 'archive'
            ? "Members lose access until an admin restores it. The space's data is preserved."
            : pendingAction?.kind === 'leave'
              ? "You'll lose access immediately. An admin would have to add you back."
              : ''
        }
        confirmText={pendingAction?.kind === 'archive' ? 'Archive' : 'Leave'}
        isLoading={actionPending}
      />
    </div>
  );
}
